/**
 * Contacts surface (live) - Add / Edit contact form on sx- materials. Covers the
 * fields the live backend persists on create/update: Name, Title, Company,
 * Email, Phone, LinkedIn, Event/Source group, Date Met, and Notes. Temperature
 * is set from the detail modal's selector and the reminder from its reminder
 * section, so neither is edited here; the contact's existing values pass through
 * unchanged. Existing source groups are offered as suggestions on the
 * Event/Source field.
 */

import { useEffect, useState } from "react";
import type { Contact, ContactInput } from "../types";
import { cleanSourceGroup, matchSourceGroups } from "../derive";
import { useDismiss } from "../use-dismiss";
import { IconChevron, IconClose } from "../contact-icons";

/**
 * Event/Source combobox: pick an existing group from the dropdown OR type a new
 * name freely (created on save). The dropdown lists ALL existing groups (cleaned
 * labels) when the field is empty or holds an existing group unchanged, and
 * filters as the user types something new. The current value is pre-selected.
 */
function SourceGroupCombobox({
  value,
  options,
  onChange,
}: {
  value: string;
  options: string[];
  onChange: (next: string) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useDismiss<HTMLDivElement>(open, setOpen);
  const exact = options.some((o) => o === value);
  const matches = matchSourceGroups(value, options);
  const creating = value.trim() !== "" && !exact;

  return (
    <div className="sxc-combo" ref={ref}>
      <input
        className="sx-field sxc-combo-input"
        placeholder="Pick or type a group, e.g. Web Summit 2025"
        value={value}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />
      <button
        type="button"
        className="sxc-combo-caret"
        aria-label={open ? "Hide source groups" : "Show source groups"}
        tabIndex={-1}
        onClick={() => setOpen((o) => !o)}
      >
        <IconChevron />
      </button>
      {open && (matches.length > 0 || creating) ? (
        <div className="sxc-combo-menu" role="listbox">
          {matches.map((o) => (
            <button
              type="button"
              key={o}
              role="option"
              aria-selected={o === value}
              className={`sxc-combo-opt${o === value ? " sxc-combo-opt--on" : ""}`}
              onClick={() => {
                onChange(o);
                setOpen(false);
              }}
            >
              {o}
            </button>
          ))}
          {creating ? (
            <p className="sxc-combo-empty">Save to create the new group "{value.trim()}".</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function ContactFormModal({
  contact,
  sourceGroups,
  submitting,
  onClose,
  onSubmit,
}: {
  contact: Contact | null;
  sourceGroups: string[];
  submitting: boolean;
  onClose: () => void;
  onSubmit: (input: ContactInput) => void;
}): JSX.Element {
  const editing = contact !== null;
  const [name, setName] = useState(contact?.name ?? "");
  const [title, setTitle] = useState(contact?.title ?? "");
  const [company, setCompany] = useState(contact?.company ?? "");
  const [email, setEmail] = useState(contact?.email ?? "");
  const [phone, setPhone] = useState(contact?.phone ?? "");
  const [linkedin, setLinkedin] = useState(contact?.linkedin ?? "");
  const [eventSource, setEventSource] = useState(cleanSourceGroup(contact?.sourceDetail));
  const [dateMet, setDateMet] = useState(contact?.dateMet ?? "");
  const [notes, setNotes] = useState(contact?.notes ?? "");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const canSave = name.trim().length > 0 && !submitting;

  const submit = () => {
    if (!canSave) return;
    onSubmit({
      name: name.trim(),
      title: title.trim(),
      company: company.trim(),
      email: email.trim(),
      phone: phone.trim() ? phone.trim() : null,
      temperature: contact?.temperature ?? "warm",
      followUpDueAt: contact?.followUpDueAt ?? null,
      notes: notes.trim(),
      linkedin: linkedin.trim() ? linkedin.trim() : null,
      eventSource: eventSource.trim() ? eventSource.trim() : null,
      dateMet: dateMet ? dateMet : null,
    });
  };

  return (
    <div className="sxc-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="sxc-modal sxc-modal--form"
        role="dialog"
        aria-modal="true"
        aria-label={editing ? "Edit contact" : "Add contact"}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="sxc-modal-close"
          aria-label="Close"
          onClick={onClose}
        >
          <IconClose />
        </button>

        <h2 className="sxc-form-title">
          {editing ? "Edit contact" : "Add contact"}
        </h2>

        <div className="sxc-form-grid">
          <label className="sxc-field-wrap">
            <span className="sx-eyebrow">Name</span>
            <input
              className="sx-field"
              value={name}
              autoFocus
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <label className="sxc-field-wrap">
            <span className="sx-eyebrow">Title</span>
            <input
              className="sx-field"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </label>
          <label className="sxc-field-wrap">
            <span className="sx-eyebrow">Company</span>
            <input
              className="sx-field"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
            />
          </label>
          <label className="sxc-field-wrap">
            <span className="sx-eyebrow">Email</span>
            <input
              className="sx-field"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label className="sxc-field-wrap">
            <span className="sx-eyebrow">Phone</span>
            <input
              className="sx-field"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </label>
          <label className="sxc-field-wrap">
            <span className="sx-eyebrow">LinkedIn</span>
            <input
              className="sx-field"
              type="url"
              placeholder="linkedin.com/in/..."
              value={linkedin}
              onChange={(e) => setLinkedin(e.target.value)}
            />
          </label>
          <label className="sxc-field-wrap">
            <span className="sx-eyebrow">Event / Source</span>
            <SourceGroupCombobox
              value={eventSource}
              options={sourceGroups}
              onChange={setEventSource}
            />
          </label>
          <label className="sxc-field-wrap">
            <span className="sx-eyebrow">Date met</span>
            <input
              className="sx-field"
              type="date"
              value={dateMet}
              onChange={(e) => setDateMet(e.target.value)}
            />
          </label>
        </div>

        <label className="sxc-field-wrap">
          <span className="sx-eyebrow">Notes</span>
          <textarea
            className="sx-field sxc-notes"
            placeholder="Relationship context, next steps, how you met..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>

        <div className="sxc-form-foot">
          <button type="button" className="sx-btn sx-btn-p3" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="sx-cta"
            disabled={!canSave}
            onClick={submit}
          >
            {submitting ? "Saving..." : editing ? "Save changes" : "Add contact"}
          </button>
        </div>
      </div>
    </div>
  );
}
